import {
  Component,
  OnChanges,
  Input,
  Output,
  EventEmitter,
  inject,
  signal,
  TemplateRef,
  WritableSignal,
} from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { MatDialog } from "@angular/material/dialog";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import { actionButtonOptions } from "@bitwarden/web-vault/app/gloss/manage/manage-accounts/accounts-add-edit/component.options";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { CabinetService } from "@bitwarden/web-vault/app/services/DataService/vault-file/cabinet.service";
import { CabinetFileError } from "@bitwarden/web-vault/app/services/DataService/vault-file/cabinet-file-error";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { TransactionStatusEnum } from "@bitwarden/web-vault/app/models/enum/transactionType";
import { VaultFileView } from "@bitwarden/web-vault/app/models/view/vault-file/vault-file.view";
import {
  CabinetFileIconMeta,
  getCabinetFileIconMeta,
} from "@bitwarden/web-vault/app/models/view/vault-file/cabinet-file-icon.util";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { DialogWrapperComponent } from "@bitwarden/web-vault/app/components/wrappers/dialog-wrapper/dialog-wrapper.component";
import { EditCabinetComponent } from "@bitwarden/web-vault/app/gloss/cabinet/edit-cabinet/edit-cabinet.component";
import { ConfirmationDialogComponent } from "@bitwarden/web-vault/app/components/confirmation-dialog/confirmation-dialog.component";
import { mobileBottomSheetConfig } from "@bitwarden/web-vault/app/components/dialog/mobile-bottom-sheet-dialog";
import { IFileValidator } from "@bitwarden/web-vault/app/models/interfaces/file-validator.interface";
import { fileTypeAllowed } from "@bitwarden/web-vault/app/shared/utils/file-upload/file-type";
import { pickFile } from "@bitwarden/web-vault/app/shared/utils/file-upload/pick-file";
import { FileValidator } from "@bitwarden/web-vault/app/validators/file-validator/file-validator";
import { firstValueFrom } from "rxjs";
import { OnBackDirective } from "@bitwarden/web-vault/app/directives/onBack.directive";

@Component({
  selector: "app-cabinet-details",
  templateUrl: "./cabinet-details.component.html",
})
export class CabinetDetailsComponent extends OnBackDirective implements OnChanges {
  private readonly i18nService = inject(I18nService);
  private readonly dialog = inject(MatDialog);
  private readonly userStoreService = inject(UserStoreService);
  private readonly cabinetService = inject(CabinetService);
  private readonly transactionStoreService = inject(TransactionStoreService);
  private readonly confirmationDialogService = inject(ConfirmationDialogService);
  private readonly document = inject(DOCUMENT);
  private readonly fileValidator: IFileValidator = new FileValidator();
  private readonly fileTypeAllowed = fileTypeAllowed;
  @Input() selectedFile!: VaultFileView;
  @Output() fileDeleted = new EventEmitter<void>();
  file: VaultFileView;
  accounts: AccountView[] = [];
  fileName: string;
  /** Whether the full-page file preview is open. */
  protected showFullPreview: WritableSignal<boolean> = signal<boolean>(false);
  protected previewFile: File | null = null;
  protected isFullPreviewLoading = false;
  /** Guards against double-clicking Replace, which would race the shared FileEncryptionService. */
  protected isReplaceFileLoading = false;
  saveButtonOptions = new ActionButton({
    ...actionButtonOptions,
    text: this.i18nService.t("save"),
    fullClass:
      "tw-shadow-manage-accounts tw-flex tw-h-12 tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-2xl tw-px-4 tw-bg-dt-custom-teal-11",
    onClick: this.submit.bind(this),
  });
  /** Precomputed on file change so the template doesn't recompute it on every change-detection pass. */
  iconMeta: CabinetFileIconMeta;

  openingBalance(accountId: string) {
    const dateStr = this.file.statementFromDate;
    return this.transactionStoreService.enrichTransaction
      .collection()
      .find(
        (tx) =>
          tx.definition === TransactionStatusEnum.opening &&
          tx.accountLink.id === accountId &&
          this.isSameDate(tx.glossPostDate.date, dateStr),
      );
  }

  closingBalance(accountId: string) {
    const dateStr = this.file.statementToDate;
    return this.transactionStoreService.enrichTransaction
      .collection()
      .find(
        (tx) =>
          tx.definition === TransactionStatusEnum.closing &&
          tx.accountLink.id === accountId &&
          this.isSameDate(tx.glossPostDate.date, dateStr),
      );
  }

  isSameDate(date1Str: string, date2Str: string): boolean {
    const d1 = new Date(date1Str);
    const d2 = new Date(date2Str);
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  }

  ngOnChanges() {
    // Selecting a different file must not leave the previous file's preview overlay open.
    this.showFullPreview.set(false);
    this.previewFile = null;

    if (!this.selectedFile) {
      return;
    }

    this.file = this.userStoreService.vaultFiles
      .vaultFileViews()
      .find((file) => file.id === this.selectedFile.id)
      ?.clone();

    this.accounts = this.userStoreService.accounts
      .accountViews()
      .filter((account) => this.file.accountIds.includes(account.id));

    this.fileName = this.file.name;
    this.iconMeta = getCabinetFileIconMeta(this.file.name);
  }

  onFileNameChanged(name: string): void {
    if (this.file) {
      this.fileName = name;
    }
  }

  onDateRangeChanged(range: { fromDate: string; toDate: string }): void {
    if (this.file) {
      this.file.statementFromDate = range.fromDate;
      this.file.statementToDate = range.toDate;
    }
  }

  getTransactionCount(accountId: string): number {
    const transactions = this.transactionStoreService.enrichTransaction.collection();
    return transactions.filter((tx) => tx.accountLink?.id === accountId && !tx.bankImportedBalance)
      .length;
  }

  linkAnotherAccount(): void {
    this.dialog.open(DialogWrapperComponent, {
      panelClass: "edit-file-dialog",
      data: {
        title: "editFile",
        showBackButton: false,
        fullyCustomised: true,
        component: EditCabinetComponent,
        componentInputs: {
          file: this.file,
          accounts: this.accounts,
        },
      },
    });
  }

  async replaceFile(): Promise<void> {
    const file = await pickFile(this.document, this.fileTypeAllowed);
    if (!file) {
      return;
    }

    if (!this.fileValidator.validate(file)) {
      this.confirmationDialogService.notify(
        NotificationEnum.error,
        "errorOccurred",
        this.fileValidator.errorMessage,
      );
      return;
    }

    this.isReplaceFileLoading = true;
    try {
      await this.cabinetService.replaceFile(this.file, file);
    } catch (e) {
      this.notifyError(e);
    } finally {
      this.isReplaceFileLoading = false;
    }
  }

  async onFullPreviewClick(): Promise<void> {
    this.isFullPreviewLoading = true;
    try {
      this.previewFile = await this.cabinetService.getFile(this.file.id);
      this.showFullPreview.set(true);
    } catch (e) {
      this.notifyError(e);
    } finally {
      this.isFullPreviewLoading = false;
    }
  }

  closeFullPreview(): void {
    this.showFullPreview.set(false);
    this.previewFile = null;
  }

  async deleteFile(template: TemplateRef<any>) {
    try {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        ...mobileBottomSheetConfig(),
        disableClose: true,
        maxWidth: "95vw",
        data: {
          templateContent: template,
          customizedButton: {
            yes: { text: "delete", class: "danger" },
            no: { text: "cancel", class: "neutral" },
          },
        },
      });
      const result = await firstValueFrom(dialogRef.afterClosed());

      if (!result) {
        return;
      }

      await this.cabinetService.deleteFile(this.file);
      this.fileDeleted.emit();
    } catch (e) {
      this.notifyError(e);
    }
  }

  private async submit() {
    if (!this.file) {
      return;
    }

    try {
      const cabinet = this.file.clone();
      cabinet.name = this.fileName;
      cabinet.statementFromDate = this.file.statementFromDate;
      cabinet.statementToDate = this.file.statementToDate;

      await this.userStoreService.vaultFiles.save(cabinet);

      this.file.name = this.fileName;
      this.iconMeta = getCabinetFileIconMeta(this.file.name);

      this.confirmationDialogService.notify(
        NotificationEnum.success,
        "succeeded",
        "updatedSuccessfully",
      );
    } catch (e) {
      this.notifyError(e);
    } finally {
      this.saveButtonOptions = new ActionButton({
        ...actionButtonOptions,
        isEnabled: true,
        text: this.i18nService.t("save"),
        fullClass:
          "border-gradient-teal tw-shadow-manage-accounts tw-flex tw-h-12 tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-2xl tw-px-4 tw-bg-dt-custom-teal-11",
        onClick: this.submit.bind(this),
      });
    }
  }

  /** Log the real error for support; show the specific reason for a failed file transfer, or a
   * generic message for anything else. */
  private notifyError(e: unknown): void {
    // eslint-disable-next-line no-console
    console.error(e);
    this.confirmationDialogService.notify(
      NotificationEnum.error,
      "errorOccurred",
      e instanceof CabinetFileError ? e.messageKey : "somethingWentWrong",
    );
  }
}
